import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ArrowLeft, Braces, FilePlus2, History, Rocket, Save, Trash2, XCircle, type LucideIcon } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, InlineAlert, Skeleton, Tabs, TabsList, TabsTrigger, Textarea, Tooltip, notifySaved, toast } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../../app/console-context";
import { CodeEditor } from "../../components/CodeEditor";
import { UnsavedChangesDialog, useUnsavedChanges } from "../../lib/useUnsavedChanges";
import { stableJson, useHistory, useUndoShortcuts } from "../forms/model";
import { VersionsPanel } from "../forms/VersionsPanel";
import { bodyOf, useDefinition, useDefinitionActions, type BaseBody, type Definition, type GenericResource } from "./api";

const STATUS_VARIANT: Record<string, "success" | "warning" | "info" | "default"> = { Published: "success", InReview: "warning", Draft: "info", Retired: "default" };

export type Patch<B> = (p: Partial<B> | ((b: B) => B), opts?: { transient?: boolean }) => void;

export interface EditorContext<B> {
  definition: Definition<B>;
  body: B;
  patch: Patch<B>;
  readOnly: boolean;
  dirty: boolean;
  /** Saves the draft; whether it is stored. */
  save: () => Promise<boolean>;
  setTab: (tab: string) => void;
}

export interface EditorTab { key: string; label: string; icon: LucideIcon }

interface Props<B extends BaseBody> {
  resource: GenericResource;
  basePath: string;
  navLabel: string;
  icon: LucideIcon;
  writePermission: string;
  tabs: EditorTab[];
  help: ReactNode;
  docsAnchor: string;
  /** Plain-language errors (block saving) and warnings. */
  lint?: (body: B) => { errors: string[]; warnings: string[] };
  /** One line under the title, e.g. "6 fields · 2 links". */
  summary?: (body: B) => string;
  stageBody?: string;
  publishedNotice?: string;
  render: (tab: string, ctx: EditorContext<B>) => ReactNode;
  /** Extra header badges. */
  badges?: (body: B) => ReactNode;
}

/** The editor shell every generic-model designer shares: loads `/:key` (optionally `?version=`), tabs in `?tab=`, and the draft → release lifecycle. */
export function DefinitionEditorPage<B extends BaseBody>(props: Props<B>) {
  const { key = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const version = params.get("version") ? Number(params.get("version")) : undefined;
  const tab = params.get("tab") ?? props.tabs[0]!.key;
  const setTab = useCallback((next: string) => setParams((p) => { p.set("tab", next); return p; }, { replace: true }), [setParams]);
  const definition = useDefinition<B>(props.resource, key, version);

  if (definition.isLoading) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-[60vh]" /></div>;
  if (definition.isError || !definition.data) return <Missing defKey={key} {...props} />;
  return <Editor key={definition.data.id} {...props} definition={definition.data} tab={tab} setTab={setTab} onOpenVersion={(v) => setParams((p) => { p.set("version", String(v)); return p; })} />;
}

function Missing<B extends BaseBody>({ defKey, basePath, navLabel }: Props<B> & { defKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBreadcrumbs([{ label: navLabel, href: basePath }, { label: defKey }]);
  return <EmptyState icon={XCircle} title={t("admin:generic.notFound")} description={t("admin:generic.notFoundBody", { key: defKey })} action={<Button variant="outline" onClick={() => navigate(basePath)}><ArrowLeft className="rtl:rotate-180" /> {t("admin:common.back")}</Button>} />;
}

function Editor<B extends BaseBody>(props: Props<B> & { definition: Definition<B>; tab: string; setTab: (t: string) => void; onOpenVersion: (v: number) => void }) {
  const { definition, tab, setTab, onOpenVersion } = props;
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canWrite = can(props.writePermission);
  const canPublish = can("platform.releases.write");
  const actions = useDefinitionActions<B>(props.resource);

  const initial = useMemo(() => bodyOf(definition), [definition]);
  const history = useHistory<B>(initial);
  useUndoShortcuts(history);
  const body = history.present;
  const savedJson = useRef(stableJson(initial));
  const dirty = stableJson(body) !== savedJson.current;
  const blocker = useUnsavedChanges(dirty);
  const editable = canWrite && definition.status === "Draft";
  const [confirm, setConfirm] = useState<"discard" | "stage" | "newVersion" | null>(null);
  const [note, setNote] = useState("");
  const issues = useMemo(() => {
    const base = props.lint?.(body) ?? { errors: [], warnings: [] };
    return body.name.trim() ? base : { ...base, errors: [t("admin:generic.lint.name"), ...base.errors] };
  }, [body, props, t]);

  const title = (locale === "ar" && body.nameAr ? body.nameAr : body.name) || definition.key;
  useBreadcrumbs(useMemo(() => [{ label: props.navLabel, href: props.basePath }, { label: title }], [props.basePath, props.navLabel, title]));
  useHelpTopic({ title: props.navLabel, docsAnchor: props.docsAnchor, body: props.help });

  const patch = useCallback<Patch<B>>((p, opts) => {
    if (!editable) return;
    history.set((b) => (typeof p === "function" ? p(b) : { ...b, ...p }), opts);
  }, [editable, history]);

  /** Saves the draft; whether it is stored (a failure was reported by useSave). */
  const save = useCallback(async (): Promise<boolean> => {
    if (!editable || !dirty) return true;
    if (issues.errors.length) { toast.error(t("admin:forms.builder.fixErrors", { count: issues.errors.length })); return false; }
    try {
      const saved = await actions.update.mutateAsync({ id: definition.id, body, changeNote: note || undefined });
      const next = bodyOf(saved);
      savedJson.current = stableJson(next);
      history.reset(next);
      setNote("");
      notifySaved(saved, t("admin:common.saved"));
      return true;
    } catch { return false; /* useSave reported it */ }
  }, [actions.update, body, definition.id, dirty, editable, history, issues.errors.length, note, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  usePaletteActions(useMemo(() => editable ? [{ id: `${props.resource}.save`, label: t("admin:common.save"), icon: Save, permission: props.writePermission, run: () => void save() }] : [], [editable, props.resource, props.writePermission, save, t]));

  const stage = async () => {
    // An unsaved draft that could not be stored is not staged: the stored version is not what the screen shows.
    if (dirty && !(await save())) return;
    try {
      const cs = await actions.stage.mutateAsync({ id: definition.id, titleHint: t("admin:generic.stageTitle", { name: title }) });
      notifySaved(cs, t("admin:forms.builder.staged"));
      setConfirm(null);
      navigate(`/releases/${cs.id}`);
    } catch { /* useSave reported it */ }
  };
  const newVersion = async () => {
    try {
      const d = await actions.newVersion.mutateAsync({ key: definition.key, changeNote: note || undefined });
      setConfirm(null); setNote("");
      notifySaved(d, t("admin:forms.builder.versionCreated", { version: d.version }));
      onOpenVersion(d.version);
    } catch { /* useSave reported it */ }
  };
  const discard = async () => {
    try { notifySaved(await actions.discard.mutateAsync(definition.id), t("admin:forms.builder.discarded")); navigate(props.basePath); } catch { /* useSave reported it */ }
  };
  const withdraw = async () => {
    try { notifySaved(await actions.withdraw.mutateAsync(definition.id), t("admin:forms.builder.withdrawn")); } catch { /* useSave reported it */ }
  };

  const Icon = props.icon;
  const tabs: EditorTab[] = [...props.tabs, { key: "code", label: t("admin:generic.tabs.code"), icon: Braces }, { key: "versions", label: t("admin:generic.tabs.versions"), icon: History }];
  const ctx: EditorContext<B> = { definition, body, patch, readOnly: !editable, dirty, save, setTab };

  return (
    <div className="-mt-2">
      <UnsavedChangesDialog blocker={blocker} />
      <div className="sticky top-14 z-20 -mx-4 mb-3 border-b border-sand-200 bg-background/90 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(props.basePath)} aria-label={t("admin:common.back")}><ArrowLeft className="rtl:rotate-180" /></Button>
          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-aegold-50 text-aegold-700"><Icon className="size-4" /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h1 className="truncate text-base font-semibold">{title}</h1>
              <Badge variant="outline" size="sm" className="font-mono">{definition.key}</Badge>
              <Badge variant="default" size="sm">v{definition.version}</Badge>
              <Badge variant={STATUS_VARIANT[definition.status] ?? "default"} size="sm" dot>{t(`admin:forms.status.${definition.status}`)}</Badge>
              {props.badges?.(body)}
              {dirty ? <Badge variant="warning" size="sm">{t("admin:common.unsaved")}</Badge> : null}
              {issues.errors.length ? <Tooltip content={issues.errors[0]}><Badge variant="danger" size="sm">{t("admin:common.issues", { count: issues.errors.length })}</Badge></Tooltip> : issues.warnings.length ? <Tooltip content={issues.warnings[0]}><Badge variant="warning" size="sm">{t("admin:forms.builder.warnings", { count: issues.warnings.length })}</Badge></Tooltip> : null}
            </div>
            {props.summary ? <p className="text-xs text-muted-foreground">{props.summary(body)}</p> : null}
          </div>
          <div className="ms-auto flex flex-wrap items-center gap-1">
            {editable ? (<>
              <Button size="sm" variant="ghost" className="text-danger-600" onClick={() => setConfirm("discard")}><Trash2 /> {t("admin:forms.builder.discard")}</Button>
              <Button data-testid="editor-save" size="sm" variant={dirty ? "gold" : "outline"} loading={actions.update.isPending} disabled={!dirty} onClick={() => void save()}><Save /> {t("admin:common.save")}</Button>
              {canPublish ? <Button data-testid="editor-stage" size="sm" variant="default" disabled={issues.errors.length > 0} onClick={() => setConfirm("stage")}><Rocket /> {t("admin:forms.builder.stage")}</Button> : null}
            </>) : null}
            {canWrite && definition.status === "InReview" ? <Button size="sm" variant="outline" onClick={withdraw} loading={actions.withdraw.isPending}>{t("admin:forms.builder.withdraw")}</Button> : null}
            {canWrite && (definition.status === "Published" || definition.status === "Retired") ? <Button data-testid="editor-new-version" size="sm" variant="gold" onClick={() => setConfirm("newVersion")}><FilePlus2 /> {t("admin:forms.builder.newVersion")}</Button> : null}
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList>{tabs.map(({ key, label, icon: TabIcon }) => <TabsTrigger key={key} value={key} data-testid={`tab-${key}`}><TabIcon className="size-3.5" /> {label}</TabsTrigger>)}</TabsList>
        </Tabs>
      </div>

      {!editable && definition.status === "Draft" && !canWrite ? <InlineAlert tone="info" className="mb-3">{t("admin:common.permissionDenied")}</InlineAlert> : null}
      {definition.status === "InReview" ? <InlineAlert tone="warning" className="mb-3">{t("admin:forms.builder.inReviewNotice")}</InlineAlert> : null}
      {definition.status === "Published" ? <InlineAlert tone="info" className="mb-3">{props.publishedNotice ?? t("admin:generic.publishedNotice")}</InlineAlert> : null}
      {editable && issues.errors.length > 0 ? <InlineAlert tone="danger" className="mb-3"><ul className="list-disc ps-4 text-sm">{issues.errors.map((e) => <li key={e}>{e}</li>)}</ul></InlineAlert> : null}

      <div className="mx-auto max-w-[1400px]">
        {tab === "code" ? <CodeEditor value={JSON.stringify(body, null, 2)} language="json" readOnly={!editable} height="560px" onChange={(v) => { try { history.set(JSON.parse(v) as B); } catch { /* keep typing */ } }} /> : null}
        {tab === "versions" ? <VersionsPanel formKey={definition.key} resource={props.resource} currentVersion={definition.version} onOpenVersion={onOpenVersion} /> : null}
        {tab !== "code" && tab !== "versions" ? props.render(tab, ctx) : null}
      </div>

      <Dialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{confirm === "discard" ? t("admin:forms.builder.discardTitle") : confirm === "stage" ? t("admin:forms.builder.stageDialogTitle") : t("admin:forms.builder.newVersionTitle")}</DialogTitle>
            <DialogDescription>{confirm === "discard" ? t("admin:forms.builder.discardBody") : confirm === "stage" ? (props.stageBody ?? t("admin:generic.stageBody")) : t("admin:forms.builder.newVersionBody", { version: definition.version + 1 })}</DialogDescription>
          </DialogHeader>
          {confirm !== "discard" ? <Field label={t("admin:common.changeNote")} hint={t("admin:common.changeNoteHint")}><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[72px]" /></Field> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>{t("admin:common.cancel")}</Button>
            {confirm === "discard" ? <Button variant="destructive" loading={actions.discard.isPending} onClick={discard}>{t("admin:forms.builder.discard")}</Button> : null}
            {confirm === "stage" ? <Button data-testid="confirm-stage" variant="default" loading={actions.stage.isPending || actions.update.isPending} onClick={stage}><Rocket /> {t("admin:forms.builder.stage")}</Button> : null}
            {confirm === "newVersion" ? <Button variant="gold" loading={actions.newVersion.isPending} onClick={newVersion}><FilePlus2 /> {t("admin:forms.builder.newVersion")}</Button> : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
